<template>
  <div>
    <h3 class="title-type">
      Email
    </h3>
    <VCheckbox
      v-model="emailCheckbox"
      hide-details
      class="d-inline-block"
    >
      <template #label>
        <span class="body-2">{{ $t('sendTo') }} {{ candidate.email }}</span>
      </template>
    </VCheckbox>
    <VTextarea
      v-model="emailMessage"
      :disabled="!emailCheckbox"
      outlined
      hide-details
      rows="3"
      class="mt-0 mb-4 white"
    />
    <!--
    SMS temporarily disabled by request.
    <h3 class="title-type">
      {{ $t('sms') }}
    </h3>
    <InterviewsQuotaCandidateSmsExceeded>
      <template #normal>
        <VCheckbox
          v-model="smsCheckbox"
          :disabled="!candidate.phone"
          hide-details
          class="d-inline-block"
        >
          <template #label>
            <span class="body-2">
              {{ $t('sendSMS') }} ({{ candidate.phone ? candidate.phone : $t('noPhoneNumber') }})
            </span>
          </template>
        </VCheckbox>
        <VTextarea
          v-model="smsMessage"
          :disabled="!smsCheckbox || !candidate.phone"
          :error="smsLimitExceeded"
          hide-details
          outlined
          rows="3"
          class="white"
        />
      </template>
      <template #exceeded>
        <VCheckbox
          v-model="smsCheckbox"
          disabled
          hide-details
          class="d-inline-block"
        >
          <template #label>
            <span class="body-2">
              {{ $t('sendSMS') }} ({{ candidate.phone ? candidate.phone : $t('noPhoneNumber') }})
            </span>
          </template>
        </VCheckbox>
        <VTextarea
          v-model="smsMessage"
          disabled
          :error="smsLimitExceeded"
          hide-details
          outlined
          class="white"
        />
      </template>
    </InterviewsQuotaCandidateSmsExceeded>

    <div
      v-if="smsCheckbox && smsCounterShown"
      :class="[{'red--text': smsLimitExceeded},'d-flex mt-2']"
    >
      <small v-if="smsLimitExceeded">
        {{ $t('smsLimitExceeded') }}
      </small>
      <div class="ml-auto">
        {{ smsCharactersCount }} <span v-if="smsMessagesCount > 1">/ {{ smsMessagesCount }}</span>
      </div>
    </div>
    -->

    <div class="mt-5">
      <VBtn
        :disabled="!isSubmitButtonAvailable"
        :loading="loading"
        height="40"
        color="success"
        class="mr-4"
        @click="submit"
      >
        {{ $t('send') }}
      </VBtn>
      <VBtn
        height="40"
        outlined
        color="grey"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </VBtn>
    </div>
  </div>
</template>

<script>
import SmsSplitter from 'split-sms';
import InterviewsQuotaCandidateSmsExceeded from '@/components/Payment/Quota/InterviewsQuotaCandidateSmsExceeded';
import ValidationMixin from '../mixins/ValidationMixin';
import RecaptchaMixin from '../mixins/RecaptchaMixin';
import Candidate from '../orm/models/Candidate/Candidate';

export default {
  name: 'CandidateContactForm',
  components: { InterviewsQuotaCandidateSmsExceeded },
  mixins: [ValidationMixin, RecaptchaMixin],
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    emailText: {
      type: String,
      default: '',
    },
    smsText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      emailCheckbox: true,
      emailMessage: this.emailText,
      smsCheckbox: false,
      smsMessage: this.smsText,
      smsMessagesCount: 1,
      smsMessagesLimit: 2,
      smsCharactersCount: 0,
      smsCharactersEncoding: 'GSM',
      charactersBeforeCounterShown: 10,
      singleMessageLength: {
        GSM: 160,
        Unicode: 70,
      },
      multiMessageLength: {
        GSM: 153,
        Unicode: 67,
      },
    };
  },
  computed: {
    isEmailCheckedAndValid() {
      return this.emailCheckbox && this.emailMessage;
    },
    isSmsCheckedAndValid() {
      return this.smsCheckbox && this.smsMessage && !this.smsLimitExceeded;
    },
    isSubmitButtonAvailable() {
      return (this.isSmsCheckedAndValid && this.isEmailCheckedAndValid)
      || (this.isSmsCheckedAndValid && !this.emailCheckbox)
      || (this.isEmailCheckedAndValid && !this.smsCheckbox);
    },
    smsCharactersLimit() {
      return this.multiMessageLength[this.smsCharactersEncoding] * this.smsMessagesLimit;
    },
    smsLimitExceeded() {
      return this.smsCharactersCount > this.smsCharactersLimit;
    },
    smsCounterShown() {
      // eslint-disable-next-line max-len
      return this.smsCharactersCount > this.singleMessageLength[this.smsCharactersEncoding] - this.charactersBeforeCounterShown;
    },
  },
  watch: {
    smsMessage(val) {
      const { characterSet, length, parts } = SmsSplitter.split(val);

      this.smsCharactersEncoding = characterSet;
      this.smsCharactersCount = length;
      this.smsMessagesCount = parts.length;
    },
  },
  methods: {
    async submit() {
      const recaptchaToken = await this.getRecaptchaToken('candidateContact');
      const payload = { recaptchaToken };
      if (this.emailCheckbox && this.emailMessage) {
        payload.mail = this.emailMessage;
      }
      if (this.smsCheckbox && this.smsMessage) {
        payload.sms = this.smsMessage;
      }
      this.withLoading(async () => {
        try {
          await Candidate.api().notify(this.candidate.id, payload);

          this.$toast.success(this.$t('messageSent'));
          this.resetForm();
          this.$emit('cancel');
        } catch (e) {
          this.$toast.error(e.message);
        }
      });
    },
    cancel() {
      this.resetForm();
      this.$emit('cancel');
    },
    resetForm() {
      this.emailCheckbox = true;
      this.emailMessage = '';
      this.smsCheckbox = false;
      this.smsMessage = '';
    },
  },
};
</script>

<style>
.title-type {
  font-size: 14px;
  line-height: 20px;
}
</style>

<i18n locale="en" lang="yaml">
  sendTo: Send to
  sendSMS: Send text message
  send: Send
  cancel: Cancel
  messageSent: Message sent
  length: characters
  smsLimitExceeded: Text message size limit exceeded
  noPhoneNumber: no phone number indicated
  sms: Text Messages
</i18n>

<i18n locale="ru" lang="yaml">
  sms: SMS
  sendTo: Отправить на
  sendSMS: Отправить SMS сообщение
  send: Отправить
  cancel: Отмена
  messageSent: Сообщение отправлено
  length: символов
  smsLimitExceeded: Превышено максимально допустимое количество в СМС
  noPhoneNumber: номер телефона не указан
</i18n>
